import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight, Grid3X3 } from "lucide-react";
import { useLicense } from "../../context/LicenseContext";
import { useUser } from "../../context/UserContext";
import {
  MODULE_CONFIG,
  MODULE_ORDER,
  getDefaultQuickLinks,
  normalizeQuickLinks,
} from "../../config/moduleNavigation";

const readStoredCollapsed = () => {
  if (typeof window === "undefined") return null;
  try {
    const saved = window.localStorage.getItem("modulesSideNavCollapsed");
    if (saved === "true") return true;
    if (saved === "false") return false;
  } catch (_) {
    // ignore localStorage errors
  }
  return null;
};

const ModulesSideNav = ({ defaultCollapsed = false }) => {
  const location = useLocation();
  const { MODULE_INFO, isModuleEnabled } = useLicense();
  const { user } = useUser();
  const listRef = useRef(null);
  const activeItemRef = useRef(null);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = readStoredCollapsed();
    return saved ?? defaultCollapsed;
  });
  const [showUnavailable, setShowUnavailable] = useState(false);

  useEffect(() => {
    const saved = readStoredCollapsed();
    if (saved === null) {
      setCollapsed(defaultCollapsed);
      return;
    }
    setCollapsed(saved);
  }, [defaultCollapsed]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("modulesSideNavCollapsed", String(next));
      } catch (_) {
        // ignore localStorage errors
      }
      return next;
    });
  };

  const centerActiveModule = useCallback((behavior = "auto") => {
    const container = listRef.current;
    const activeItem = activeItemRef.current;
    if (!container || !activeItem) return;

    const canScrollX = container.scrollWidth > container.clientWidth + 1;
    const canScrollY = container.scrollHeight > container.clientHeight + 1;
    if (!canScrollX && !canScrollY) return;

    const targetLeft = canScrollX
      ? activeItem.offsetLeft +
        activeItem.offsetWidth / 2 -
        container.clientWidth / 2
      : container.scrollLeft;
    const targetTop = canScrollY
      ? activeItem.offsetTop +
        activeItem.offsetHeight / 2 -
        container.clientHeight / 2
      : container.scrollTop;

    const maxLeft = container.scrollWidth - container.clientWidth;
    const maxTop = container.scrollHeight - container.clientHeight;
    const nextLeft = Math.min(Math.max(targetLeft, 0), maxLeft);
    const nextTop = Math.min(Math.max(targetTop, 0), maxTop);

    if (
      Math.abs(container.scrollLeft - nextLeft) < 1 &&
      Math.abs(container.scrollTop - nextTop) < 1
    ) {
      return;
    }

    container.scrollTo({ left: nextLeft, top: nextTop, behavior });
  }, []);

  const modules = useMemo(() => {
    return MODULE_ORDER.map((key) => {
      const info = MODULE_INFO[key];
      const config = MODULE_CONFIG[key];
      if (!info || !config) return null;
      const features = Array.isArray(info.features) ? info.features : [];
      const visibleFeatures = features.slice(0, 3);
      return {
        key,
        name: info.name,
        description: info.description,
        features: visibleFeatures,
        remainingFeatures: Math.max(features.length - visibleFeatures.length, 0),
        enabled: isModuleEnabled(key),
        ...config,
      };
    }).filter(Boolean);
  }, [MODULE_INFO, isModuleEnabled]);

  const { enabledModules, disabledModules } = useMemo(() => {
    const enabled = [];
    const disabled = [];
    modules.forEach((module) => {
      if (module.enabled) {
        enabled.push(module);
      } else {
        disabled.push(module);
      }
    });
    return { enabledModules: enabled, disabledModules: disabled };
  }, [modules]);

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => centerActiveModule("auto"));
    const timer = window.setTimeout(() => centerActiveModule("auto"), 200);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [centerActiveModule, collapsed, location.pathname, modules.length]);

  const renderModuleCard = (module) => {
    const active = module.isActive(location.pathname);
    const Icon = module.icon;
    const statusLabel = module.enabled ? "Enabled" : "Disabled";
    const quickLinkSettings = user?.settings?.module_quick_links || {};
    const hasCustomQuickLinks = Object.prototype.hasOwnProperty.call(
      quickLinkSettings,
      module.key,
    );
    const defaultQuickLinks = getDefaultQuickLinks(module.key);
    const customQuickLinks = normalizeQuickLinks(
      module.key,
      quickLinkSettings[module.key],
    );
    const quickLinkPaths = hasCustomQuickLinks
      ? customQuickLinks.length
        ? customQuickLinks
        : defaultQuickLinks
      : defaultQuickLinks;
    const quickLinks = quickLinkPaths
      .map((path) => module.pages?.find((page) => page.path === path))
      .filter(Boolean);
    const showQuickLinks = !active && module.enabled && quickLinks.length > 0;
    return (
      <div
        key={module.key}
        ref={active ? activeItemRef : null}
        className={`group relative rounded-xl border p-3 transition md:min-w-0 ${
          collapsed ? "min-w-[72px] justify-center" : "min-w-[230px]"
        } ${
          active
            ? "border-slate-200 bg-white/80 shadow-sm dark:border-slate-700/70 dark:bg-slate-900/70"
            : "border-transparent hover:bg-white/70 dark:hover:bg-slate-800/60"
        } ${module.enabled ? "" : "opacity-70"}`}
      >
        {active && (
          <span
            className={`absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-full ${module.accent}`}
          />
        )}
        <div
          className={`flex w-full ${
            collapsed ? "items-center justify-center" : "items-start justify-between"
          } gap-3`}
        >
          <Link
            to={module.path}
            className={`flex ${collapsed ? "items-center" : "items-start"} gap-3`}
            title={collapsed ? module.name : undefined}
            aria-label={collapsed ? module.name : undefined}
          >
            <span className={`rounded-lg p-2 text-white shadow-lg ${module.iconBg}`}>
              <Icon className="h-4 w-4" />
            </span>
            {!collapsed && (
              <div>
                <div
                  className={`text-sm font-semibold ${
                    active
                      ? "text-slate-900 dark:text-slate-100"
                      : "text-slate-700 dark:text-slate-200"
                  }`}
                >
                  {module.name}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {module.description}
                </div>
              </div>
            )}
          </Link>
          {!collapsed && (
            <span
              className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                module.enabled
                  ? "border-emerald-200/70 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/40 dark:text-emerald-200"
                  : "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400"
              }`}
            >
              {statusLabel}
            </span>
          )}
        </div>
        {collapsed && (
          <span
            className={`absolute right-2 top-2 h-2 w-2 rounded-full ${
              module.enabled ? "bg-emerald-400" : "bg-slate-400"
            }`}
          />
        )}
        {!collapsed && (
          <>
            {active && (
              <div className="mt-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Capabilities
                </div>
                <div className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                  {module.features.map((feature) => {
                    const link = module.featureLinks?.[feature];
                    return link ? (
                      <Link
                        key={feature}
                        to={link}
                        className="block text-slate-600 transition hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-200"
                      >
                        {feature}
                      </Link>
                    ) : (
                      <div key={feature}>{feature}</div>
                    );
                  })}
                  {module.remainingFeatures > 0 && (
                    <div className="text-slate-400">
                      +{module.remainingFeatures} more
                    </div>
                  )}
                </div>
              </div>
            )}
            {showQuickLinks && (
              <div className="mt-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Quick Links
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {quickLinks.map((page) => (
                    <Link
                      key={page.label}
                      to={page.path}
                      className="rounded-full bg-white/80 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-white hover:text-slate-900 dark:bg-slate-800/70 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    >
                      {page.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {active && module.pages?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Pages
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {module.pages
                    .filter((page) => {
                      // If page has no roles restriction, show to everyone
                      if (!page.roles || page.roles.length === 0) return true;
                      // Check if user has any of the required roles
                      const userRoles = (user?.role_slugs || []).map((r) =>
                        String(r).toLowerCase()
                      );
                      return page.roles.some((role) =>
                        userRoles.includes(role.toLowerCase())
                      );
                    })
                    .map((page) => {
                      const pageActive = page.isActive(location.pathname);
                      return (
                        <Link
                          key={page.label}
                          to={page.path}
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                            pageActive
                              ? "bg-slate-900 text-white shadow-sm dark:bg-slate-100 dark:text-slate-900"
                              : "bg-white/80 text-slate-600 hover:bg-white hover:text-slate-900 dark:bg-slate-800/70 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                          }`}
                        >
                          {page.label}
                        </Link>
                      );
                    })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <aside className={`${collapsed ? "md:w-20" : "md:w-80"} shrink-0`}>
      <div className={`group/sidebar relative overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-br from-blue-50 via-indigo-50 to-emerald-100/70 p-4 shadow-xl dark:border-slate-700/70 dark:from-slate-900 dark:via-blue-950 dark:to-emerald-950/40 md:sticky md:top-24 ${
        collapsed ? "md:overflow-visible" : ""
      }`}>
        <div className="absolute -right-10 -top-8 h-24 w-24 rounded-full bg-blue-200/35 blur-2xl dark:bg-blue-500/10" />
        <div className="absolute -left-8 top-16 h-20 w-20 rounded-full bg-emerald-200/35 blur-2xl dark:bg-emerald-500/10" />
        <div className="relative">
          <div
            className={`flex items-start justify-between gap-2 ${
              collapsed
                ? "md:flex-col md:items-center md:justify-start md:gap-3"
                : ""
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-slate-900 p-2 text-white shadow-lg shadow-slate-900/20 dark:bg-slate-100 dark:text-slate-900">
                <Grid3X3 className="h-5 w-5" />
              </span>
              {!collapsed && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300">
                    Workspace
                  </div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Modules
                  </div>
                </div>
              )}
            </div>
            <div
              className={`relative ${
                collapsed
                  ? "md:absolute md:-right-7 md:top-3 md:z-20 md:translate-y-1 md:opacity-0 md:pointer-events-none md:group-hover/sidebar:translate-y-0 md:group-hover/sidebar:opacity-100 md:group-hover/sidebar:pointer-events-auto md:focus-within:translate-y-0 md:focus-within:opacity-100 md:focus-within:pointer-events-auto"
                  : ""
              }`}
            >
              <button
                type="button"
                className="peer rounded-lg border border-slate-200/70 bg-white/80 p-1 text-slate-600 shadow-sm transition hover:bg-white dark:border-slate-700/70 dark:bg-slate-900/70 dark:text-slate-200"
                onClick={toggleCollapsed}
                aria-label={
                  collapsed ? "Expand navigation" : "Collapse navigation"
                }
              >
                {collapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </button>
              {collapsed && (
                <span className="pointer-events-none absolute left-1/2 top-full mt-2 z-20 hidden -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-full bg-slate-900/90 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white shadow-lg opacity-0 transition md:block peer-hover:translate-y-0 peer-hover:opacity-100 peer-focus-visible:translate-y-0 peer-focus-visible:opacity-100 dark:bg-slate-100 dark:text-slate-900">
                  Expand navigation
                </span>
              )}
            </div>
          </div>
          {!collapsed && (
            <div className="mt-3 text-xs text-slate-600 dark:text-slate-300">
              Jump between planning areas and active workstreams.
            </div>
          )}
          <nav className="mt-4">
            <div
              ref={listRef}
              className={`flex gap-3 overflow-x-auto pb-2 md:max-h-[calc(100vh-14rem)] md:flex-col md:overflow-y-auto md:overflow-x-hidden md:pb-0 rounded-xl border border-slate-200/70 bg-white/70 p-2 dark:border-slate-700/70 dark:bg-slate-900/60 ${
                collapsed ? "md:items-center" : ""
              }`}
            >
              {enabledModules.map(renderModuleCard)}
              {disabledModules.length > 0 && !collapsed && (
                <button
                  type="button"
                  onClick={() => setShowUnavailable((prev) => !prev)}
                  className="flex w-full items-center justify-between gap-2 rounded-xl border border-dashed border-slate-200/80 bg-white/70 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 transition hover:bg-white dark:border-slate-700/70 dark:bg-slate-900/60 dark:text-slate-300"
                  aria-expanded={showUnavailable}
                >
                  <span>Unavailable</span>
                  <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                    {disabledModules.length}
                  </span>
                  {showUnavailable ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              )}
              {showUnavailable && disabledModules.map(renderModuleCard)}
            </div>
          </nav>
        </div>
      </div>
    </aside>
  );
};

export default ModulesSideNav;
